import { useEffect, useState } from 'react';
import {
  schedulerListGetApi,
  SchedulerType,
  schedulerUpdatePostApi,
} from '../../../api/apiConfig';
import useFetch from '../../../api/useFetch';
import styled from 'styled-components';
import {
  changeHourNumberToTimeString,
  changeTimeStringToHourNumber,
} from '../../../function/initData';
import { useHeaderButtons } from '../../../component/header/HeaderButtonProvider';
import SquareButton from '../../../component/button/SquareButton';

const oneHourMilliSec = 3600000;

//시간/주기 드롭다운 옵션
const executionTypeOptions = [
  {
    name: '시간',
    value: 'TIME',
  },
  {
    name: '주기',
    value: 'INTERVAL',
  },
];

//오전/오후 드롭다운 옵션
const meridiemOptions = [
  {
    name: '오전',
    value: 'AM',
  },
  {
    name: '오후',
    value: 'PM',
  },
];

//시간간격 드롭다운 옵션
const intervalOptions = [
  {
    name: '30분',
    value: oneHourMilliSec / 2,
  },
  {
    name: '1시간',
    value: oneHourMilliSec * 1,
  },
  {
    name: '2시간',
    value: oneHourMilliSec * 2,
  },
  {
    name: '3시간',
    value: oneHourMilliSec * 3,
  },
  {
    name: '4시간',
    value: oneHourMilliSec * 4,
  },
  {
    name: '5시간',
    value: oneHourMilliSec * 5,
  },
  {
    name: '6시간',
    value: oneHourMilliSec * 6,
  },
  {
    name: '10시간',
    value: oneHourMilliSec * 10,
  },
];

//시간 드롭다운 옵션
const timeOptions = [
  {
    name: '1시',
    value: 1,
  },
  {
    name: '2시',
    value: 2,
  },
  {
    name: '3시',
    value: 3,
  },
  {
    name: '4시',
    value: 4,
  },
  {
    name: '5시',
    value: 5,
  },
  {
    name: '6시',
    value: 6,
  },
  {
    name: '7시',
    value: 7,
  },
  {
    name: '8시',
    value: 8,
  },
  {
    name: '9시',
    value: 9,
  },
  {
    name: '10시',
    value: 10,
  },
  {
    name: '11시',
    value: 11,
  },
  {
    name: '12시',
    value: 12,
  },
];

export const SchedulerManage = () => {
  const { setButtons } = useHeaderButtons();

  const [schedulerList, setSchedulerList] = useState<SchedulerType[]>([]);

  const { fetch: fetchSchedulerListGetApi } = useFetch({
    fetchFunction: schedulerListGetApi,
    onSuccess: (data: SchedulerType[]) => setSchedulerList(data),
  });

  const { fetch: fetchSchedulerUpdatePostApi } = useFetch({
    fetchFunction: schedulerUpdatePostApi,
  });

  useEffect(() => {
    fetchSchedulerListGetApi();
  }, []);

  useEffect(() => {
    //헤더에 버튼 추가
    setButtons(
      <>
        <SquareButton
          text='저장하기'
          colorType='ACTIVE'
          onClick={() => {
            fetchSchedulerUpdatePostApi(schedulerList);
          }}
        />
      </>
    );
    // 페이지가 바뀌면 버튼 초기화
    return () => setButtons(null);
  }, [setButtons, schedulerList]);

  function schedulerOptionChanged<K extends keyof SchedulerType>(
    schedulerId: number,
    key: K,
    value: SchedulerType[K]
  ) {
    //리스트에서 타겟 검색
    const targetScheduler = schedulerList.find(
      (schedulerState) => schedulerState.id === schedulerId
    );
    //타겟이 없거나, props로 받은 key값이 target에 없다면 리턴
    if (!targetScheduler) return;
    if (!(key in targetScheduler)) {
      console.error(`Key "${key}" does not exist in SchedulerType.`);
      return;
    }

    // schedulerList에서 id를 기준으로 항목 업데이트
    setSchedulerList((prev) =>
      prev.map((scheduler) =>
        scheduler.id === schedulerId
          ? {
              ...scheduler,
              [key]: value, // 업데이트
            }
          : scheduler
      )
    );
  }

  const TimeRow = ({ scheduler }: { scheduler: SchedulerType }) => {
    let meridiem: string | null = null;
    let hour: number | null = null;

    //오전, 오후 값과 시간
    const timeData = changeTimeStringToHourNumber(scheduler.executionTime);
    //기존값이 없으면 오전5시를 기본값으로 설정
    if (!timeData) {
      schedulerOptionChanged(scheduler.id!, 'executionTime', '05:00:00');
      return;
    } else {
      //기존값이 있으면
      meridiem = timeData.meridiem;
      hour = timeData.hour;
    }

    return (
      <FlexRowHolder className='spacing'>
        <div>매일</div>
        <StyledSelect
          id='meridiem-dropdown'
          value={meridiem}
          onChange={(e) => {
            schedulerOptionChanged(
              scheduler.id!,
              'executionTime',
              changeHourNumberToTimeString(e.target.value, hour)
            );
          }}
        >
          {meridiemOptions.map((meridiem) => (
            <StyledOption value={meridiem.value}>{meridiem.name}</StyledOption>
          ))}
        </StyledSelect>
        <StyledSelect
          id='interval-dropdown'
          value={hour}
          onChange={(e) => {
            schedulerOptionChanged(
              scheduler.id!,
              'executionTime',
              changeHourNumberToTimeString(meridiem, Number(e.target.value))
            );
          }}
        >
          {timeOptions.map((time) => (
            <StyledOption value={time.value}>{time.name}</StyledOption>
          ))}
        </StyledSelect>
        <div>마다 실행됩니다</div>
      </FlexRowHolder>
    );
  };

  const IntervalRow = ({ scheduler }: { scheduler: SchedulerType }) => {
    let fixedRate: number | null = null;

    if (!scheduler.fixedRate) {
      schedulerOptionChanged(scheduler.id!, 'fixedRate', oneHourMilliSec);
      return;
    } else {
      fixedRate = scheduler.fixedRate;
    }

    return (
      <FlexRowHolder className='spacing'>
        <StyledSelect
          id='meridiem-dropdown'
          value={fixedRate}
          onChange={(e) => {
            schedulerOptionChanged(
              scheduler.id!,
              'fixedRate',
              Number(e.target.value)
            );
          }}
        >
          {intervalOptions.map((interval) => (
            <StyledOption value={interval.value}>{interval.name}</StyledOption>
          ))}
        </StyledSelect>
        <div>마다 실행됩니다</div>
      </FlexRowHolder>
    );
  };

  return (
    <>
      {schedulerList.length > 0 &&
        schedulerList.map((scheduler) => (
          <Content>
            <Description className='spacing'>
              {scheduler.taskDescription}
            </Description>
            <FlexRowHolder className='spacing'>
              {executionTypeOptions.map((option) => (
                <label key={option.value}>
                  <input
                    type='radio'
                    checked={scheduler.executionType === option.value}
                    name={`execution-type-option-${scheduler.id}`}
                    value={option.value}
                    onChange={(e) =>
                      setSchedulerList((prev) =>
                        prev.map((schedulerState) =>
                          schedulerState.id === scheduler.id
                            ? { ...scheduler, executionType: e.target.value }
                            : schedulerState
                        )
                      )
                    }
                  />
                  {option.name}
                </label>
              ))}
            </FlexRowHolder>
            {scheduler.executionType === 'TIME' ? (
              <TimeRow scheduler={scheduler} />
            ) : (
              <IntervalRow scheduler={scheduler} />
            )}
          </Content>
        ))}
    </>
  );
};

const Content = styled.div`
  background-color: white;
  margin-bottom: 20px;
  width: 800px;
  /* height: 300px; */
  border-radius: 5px;
  .spacing {
    padding: 5px 0px;
    margin: 0px 8px;
  }
`;

const Description = styled.div`
  font-size: 19px;
  color: ${({ theme }) => theme.colors.charcoal};
  border-bottom: ${({ theme }) => `1px solid ${theme.colors.charcoal}`};
  font-weight: 700;
`;

const FlexRowHolder = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const StyledSelect = styled.select`
  padding: 3px;
`;

const StyledOption = styled.option`
  padding: 3px;
`;
